import { RefObject, useCallback, useEffect, useRef } from 'react';

export type UseVideoWatchedSecondsHandle = {
  getWatchedSeconds: () => number;
  reset: () => void;
};

type Options = {
  videoRef: RefObject<HTMLVideoElement | null>;
  /**
   * Fires whenever the accrued whole-second count changes (so at most ~1/s
   * during playback, never on pause). View tracking mirrors this into a ref
   * that outlives the player: `getWatchedSeconds` is only reachable through a
   * ref that React detaches on unmount, which is exactly when the closing
   * document needs its final duration.
   */
  onWatchedSecondsChange?: (seconds: number) => void;
};

export default function useVideoWatchedSeconds({
  videoRef,
  onWatchedSecondsChange,
}: Options): UseVideoWatchedSecondsHandle {
  const watchedRef = useRef(0);
  const lastTimeRef = useRef<number | null>(null);
  const playingRef = useRef(false);
  const visibleRef = useRef(true);
  const intersectingRef = useRef(true);
  const reportedWholeRef = useRef(0);
  const watchedChangeCbRef = useRef(onWatchedSecondsChange);
  watchedChangeCbRef.current = onWatchedSecondsChange;

  const isActive = useCallback(
    () => playingRef.current && visibleRef.current && intersectingRef.current,
    [],
  );

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;

    visibleRef.current = !document.hidden;

    const handlePlay = () => {
      playingRef.current = true;
      lastTimeRef.current = el.currentTime;
    };
    const handlePauseOrEnd = () => {
      playingRef.current = false;
      lastTimeRef.current = null;
    };
    const handleSeeking = () => {
      lastTimeRef.current = null;
    };
    const handleSeeked = () => {
      lastTimeRef.current = el.currentTime;
    };
    const handleTimeUpdate = () => {
      if (!isActive()) return;
      const now = el.currentTime;
      const prev = lastTimeRef.current;
      lastTimeRef.current = now;
      if (prev === null) return;
      const delta = now - prev;
      if (delta <= 0) return;
      watchedRef.current += delta;
      const whole = Math.floor(watchedRef.current);
      if (whole !== reportedWholeRef.current) {
        reportedWholeRef.current = whole;
        watchedChangeCbRef.current?.(whole);
      }
    };

    const handleVisibility = () => {
      visibleRef.current = !document.hidden;
      if (!visibleRef.current) lastTimeRef.current = null;
      else if (playingRef.current) lastTimeRef.current = el.currentTime;
    };

    el.addEventListener('play', handlePlay);
    el.addEventListener('playing', handlePlay);
    el.addEventListener('pause', handlePauseOrEnd);
    el.addEventListener('ended', handlePauseOrEnd);
    el.addEventListener('seeking', handleSeeking);
    el.addEventListener('seeked', handleSeeked);
    el.addEventListener('timeupdate', handleTimeUpdate);
    document.addEventListener('visibilitychange', handleVisibility);

    let observer: IntersectionObserver | undefined;
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        intersectingRef.current = !!entry?.isIntersecting;
        if (!intersectingRef.current) lastTimeRef.current = null;
        else if (playingRef.current) lastTimeRef.current = el.currentTime;
      });
      observer.observe(el);
    }

    return () => {
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('playing', handlePlay);
      el.removeEventListener('pause', handlePauseOrEnd);
      el.removeEventListener('ended', handlePauseOrEnd);
      el.removeEventListener('seeking', handleSeeking);
      el.removeEventListener('seeked', handleSeeked);
      el.removeEventListener('timeupdate', handleTimeUpdate);
      document.removeEventListener('visibilitychange', handleVisibility);
      observer?.disconnect();
    };
  }, [isActive, videoRef]);

  const getWatchedSeconds = useCallback(
    () => Math.max(0, Math.floor(watchedRef.current)),
    [],
  );

  const reset = useCallback(() => {
    watchedRef.current = 0;
    lastTimeRef.current = null;
    reportedWholeRef.current = 0;
    watchedChangeCbRef.current?.(0);
  }, []);

  return { getWatchedSeconds, reset };
}
